import GTMService from 'services/ga4';
import {CartState, ColaPromoType} from 'store/types';
import {CartItemsType, ICartItem, IRestaurant, StorageKeyEnum} from 'types';
import {create} from 'zustand';
import {devtools, persist} from 'zustand/middleware';
import {immer} from 'zustand/middleware/immer';

import {useOrderProviderStore} from './orderStore';

export type CartActions = {
  setCartItems: (cartItems: CartItemsType) => void;
  editCartItem: (cartItem: ICartItem) => void;
  changeCartItemQuantity: (cartItemId: string, quantity: number) => void;
  updateTimestamp: () => void;
  clearAll: (disableTracking?: boolean) => void;
  setProvider: (provider: IRestaurant) => void;
  deleteCartItemById: (id: string) => CartItemsType;
  resetTimestamp: () => void;
  setColaPromo: (colaPromo: ColaPromoType) => void;
  // Metro cart actions
  setMetroCartItems: (cartItems: CartItemsType) => void;
  editMetroCartItem: (cartItem: ICartItem) => void;
  changeMetroCartItemQuantity: (cartItemId: string, quantity: number) => void;
  updateMetroTimestamp: () => void;
  clearMetroAll: (disableTracking?: boolean) => void;
  setMetroProvider: (provider: IRestaurant) => void;
  deleteMetroCartItemById: (id: string) => CartItemsType;
  resetMetroTimestamp: () => void;
  setMetroColaPromo: (colaPromo: ColaPromoType) => void;
};

export type CartStore = CartState & CartActions;

const initialState: CartState = {
  cartItems: [],
  provider: undefined,
  deliveryDetails: {
    deliveryAmount: 0,
    remainingAmount: 0,
  },
  colaPromo: {
    cola_promo_active: false,
    cola_promo_threshold: 0,
  },
  // Metro cart initial state
  metro_cartItems: [],
  metro_provider: undefined,
  metro_deliveryDetails: {
    deliveryAmount: 0,
    remainingAmount: 0,
  },
  metro_colaPromo: {
    cola_promo_active: false,
    cola_promo_threshold: 0,
  },
};

/**
 * A small, fast, and scalable bearbones state management solution
 *
 * https://docs.pmnd.rs/zustand/integrations/immer-middleware
 */
export const useCartStore = create<CartStore>()(
  immer(
    devtools(
      persist(
        (set, get) => ({
          ...initialState,
          updateTimestamp: () =>
            set((state) => {
              state.timestamp = new Date();
            }),
          resetTimestamp: () => {
            set((state) => {
              state.timestamp = new Date();
            });
          },
          setCartItems: (cartItems: CartItemsType) => {
            return set((state) => {
              state.cartItems = cartItems;
            });
          },
          editCartItem: (cartItem: ICartItem) => {
            set((state) => {
              const id = cartItem.id as unknown as number;
              if (cartItem.id && state.cartItems[id]) {
                state.cartItems[id] = cartItem;
              }
            });
          },
          changeCartItemQuantity: (cartItemId: string, quantity: number) => {
            if (quantity >= 0) {
              if (quantity == 0) {
                set((state) => {
                  const productToRemove1 = get().cartItems.find((item) => item.id === cartItemId);
                  if (productToRemove1)
                    GTMService.removeFromCart([productToRemove1 as ICartItem], state?.provider?.slug ?? '');

                  const orderStore = useOrderProviderStore.getState();
                  if (state.cartItems.length === 1) {
                    state.timestamp = undefined;
                    state.cartItems = [];
                    state.provider = undefined;
                    orderStore.resetState();
                  }
                  state.cartItems = state.cartItems.filter((product: ICartItem) => product.id !== cartItemId);
                });
              } else {
                set((state) => {
                  const productToEdit = get().cartItems.find((item) => item.id === cartItemId);
                  if (productToEdit && productToEdit.quantity < quantity)
                    GTMService.addToCart([{...productToEdit, quantity: 1} as ICartItem], state?.provider?.slug ?? '');
                  if (productToEdit && productToEdit.quantity > quantity)
                    GTMService.removeFromCart(
                      [{...productToEdit, quantity: 1} as ICartItem],
                      state?.provider?.slug ?? '',
                    );

                  state.cartItems = state.cartItems.map((item: ICartItem) =>
                    item.id === cartItemId
                      ? {
                          ...item,
                          quantity,
                        }
                      : item,
                  );
                });
              }
            }
          },
          deleteCartItemById: (cartItemId: string) => {
            set((state) => {
              const productToRemove = get().cartItems.find((item) => item.id === cartItemId);
              if (productToRemove)
                GTMService.removeFromCart([productToRemove as ICartItem], state?.provider?.slug ?? '');
              state.cartItems = state.cartItems.filter((product: ICartItem) => product.id !== cartItemId);
            });
            return get().cartItems;
          },
          clearAll: (disableTracking?: boolean) => {
            set((state) => {
              if (!disableTracking) GTMService.removeFromCart(get().cartItems, state?.provider?.slug ?? '');
              state.timestamp = undefined;
              state.cartItems = [];
              state.provider = undefined;
            });
          },
          setProvider: (provider: IRestaurant) => {
            set((state) => {
              state.provider = provider;
            });
          },
          setColaPromo: ({cola_promo_active, cola_promo_threshold}: ColaPromoType) =>
            set((state) => {
              state.colaPromo = {
                cola_promo_active,
                cola_promo_threshold,
              };
            }),
          // Metro cart actions
          updateMetroTimestamp: () =>
            set((state) => {
              state.metro_timestamp = new Date();
            }),
          resetMetroTimestamp: () => {
            set((state) => {
              state.metro_timestamp = new Date();
            });
          },
          setMetroCartItems: (cartItems: CartItemsType) => {
            return set((state) => {
              state.metro_cartItems = cartItems;
            });
          },
          editMetroCartItem: (cartItem: ICartItem) => {
            set((state) => {
              const id = cartItem.id as unknown as number;
              if (cartItem.id && state.metro_cartItems[id]) {
                state.metro_cartItems[id] = cartItem;
              }
            });
          },
          changeMetroCartItemQuantity: (cartItemId: string, quantity: number) => {
            if (quantity >= 0) {
              if (quantity == 0) {
                set((state) => {
                  const productToRemove1 = get().metro_cartItems.find((item) => item.id === cartItemId);
                  if (productToRemove1)
                    GTMService.removeFromCart([productToRemove1 as ICartItem], state?.metro_provider?.slug ?? '');

                  const orderStore = useOrderProviderStore.getState();
                  if (state.metro_cartItems.length === 1) {
                    state.metro_timestamp = undefined;
                    state.metro_cartItems = [];
                    state.metro_provider = undefined;
                    orderStore.resetState();
                  }
                  state.metro_cartItems = state.metro_cartItems.filter(
                    (product: ICartItem) => product.id !== cartItemId,
                  );
                });
              } else {
                set((state) => {
                  const productToEdit = get().metro_cartItems.find((item) => item.id === cartItemId);
                  if (productToEdit && productToEdit.quantity < quantity)
                    GTMService.addToCart(
                      [{...productToEdit, quantity: 1} as ICartItem],
                      state?.metro_provider?.slug ?? '',
                    );
                  if (productToEdit && productToEdit.quantity > quantity)
                    GTMService.removeFromCart(
                      [{...productToEdit, quantity: 1} as ICartItem],
                      state?.metro_provider?.slug ?? '',
                    );

                  state.metro_cartItems = state.metro_cartItems.map((item: ICartItem) =>
                    item.id === cartItemId
                      ? {
                          ...item,
                          quantity,
                        }
                      : item,
                  );
                });
              }
            }
          },
          deleteMetroCartItemById: (cartItemId: string) => {
            set((state) => {
              const productToRemove = get().metro_cartItems.find((item) => item.id === cartItemId);
              if (productToRemove)
                GTMService.removeFromCart([productToRemove as ICartItem], state?.metro_provider?.slug ?? '');
              state.metro_cartItems = state.metro_cartItems.filter((product: ICartItem) => product.id !== cartItemId);
            });
            return get().metro_cartItems;
          },
          clearMetroAll: (disableTracking?: boolean) => {
            set((state) => {
              if (!disableTracking) GTMService.removeFromCart(get().metro_cartItems, state?.metro_provider?.slug ?? '');
              state.metro_timestamp = undefined;
              state.metro_cartItems = [];
              state.metro_provider = undefined;
            });
          },
          setMetroProvider: (provider: IRestaurant) => {
            set((state) => {
              state.metro_provider = provider;
            });
          },
          setMetroColaPromo: ({cola_promo_active, cola_promo_threshold}: ColaPromoType) =>
            set((state) => {
              state.metro_colaPromo = {
                cola_promo_active,
                cola_promo_threshold,
              };
            }),
        }),
        {
          name: StorageKeyEnum.CART,
          partialize: (state) => Object.fromEntries(Object.entries(state).filter(([key]) => ![''].includes(key))),
        },
      ),
    ),
  ),
);
